import { useState } from 'preact/hooks';
import { BtnCopy, Button } from '@components/buttons';
import { IcoCalendar, IcoCheck, IcoExternalLink } from '@components/icons';
import { useCurrentUser } from 'client/lib/auth';
import { useAsyncData } from 'client/lib/hooks/use-async-data';
import { rpx } from 'client/lib/rpx-client';
import { InputField } from '@components/calendar-form';
import { showToast } from '@components/toaster';
import type { DomainRow } from 'server/types';
import { CORE_DOMAIN } from 'shared/consts';
import { StatusPill } from './signup-wizard/status-pill';

type DomainUIState = {
  showDomainForm: boolean;
  domainInput: string;
  submitting: boolean;
  addedDomains: DomainRow[];
};

function GoToLogin() {
  return (
    <div class="flex flex-col h-full">
      <div class="flex-1 flex flex-col justify-center items-center gap-6">
        <div class="text-center">
          <h2 class="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
          <p class="text-gray-600">Please log in to continue setting up your calendar.</p>
        </div>
        <Button
          href="/calendar/login"
          class="bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-md font-semibold"
        >
          Go to Login
        </Button>
      </div>
    </div>
  );
}

function Loading() {
  return (
    <div class="flex flex-col h-full">
      <div class="flex-1 flex flex-col justify-center items-center gap-4">
        <div class="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
        <p class="text-gray-600 text-sm">Loading your domains...</p>
      </div>
    </div>
  );
}

function DomainForm({
  state,
  setState,
  handleAddDomain,
}: {
  state: DomainUIState;
  setState: (updater: (prev: DomainUIState) => DomainUIState) => void;
  handleAddDomain: (e: Event) => Promise<void>;
}) {
  return (
    <div class="flex flex-col">
      {/* Content area */}
      <div class="flex flex-col items-center gap-4 pb-8">
        <StatusPill text="Domain setup" theme="indigo" />

        <div class="text-center">
          <h2 class="text-3xl font-bold text-gray-900 mb-3">Your Professional Web Address</h2>
          <p class="text-gray-600 leading-relaxed">
            This is the final step to take your page live. Set up a custom domain where clients can
            easily find and book with you.
          </p>
        </div>

        {/* Setup Steps Card */}
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 w-full">
          <h3 class="text-lg font-semibold text-blue-900 mb-4">Setup Steps:</h3>

          <div class="flex flex-col gap-4">
            <div class="flex gap-3">
              <div class="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                1
              </div>
              <div class="text-blue-800">Go to your domain provider (GoDaddy, Namecheap, etc.)</div>
            </div>

            <div class="flex gap-3">
              <div class="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                2
              </div>
              <div class="text-blue-800">
                Add a CNAME record pointing your domain to{' '}
                <BtnCopy class="font-semibold rounded-lg border" value={CORE_DOMAIN} margin="mt-1">
                  {CORE_DOMAIN}
                </BtnCopy>
              </div>
            </div>

            <div class="flex gap-3">
              <div class="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                3
              </div>
              <div class="text-blue-800">Come back here and click "Add Domain" below</div>
            </div>
          </div>
        </div>

        {/* Domain form */}
        <form onSubmit={handleAddDomain} class="w-full">
          <div class="mb-4">
            <InputField
              name="domain"
              title="Custom Domain"
              type="text"
              placeholder="book.yourname.com"
              value={state.domainInput}
              onInput={(e) =>
                setState((prev) => ({
                  ...prev,
                  domainInput: (e.target as HTMLInputElement).value,
                }))
              }
              required
              pattern="^[a-zA-Z0-9\\-_.]+$"
              subtitle="Enter a valid domain name (e.g., book.yourname.com)"
              fullWidth
              autoFocus
            />
          </div>

          <div class="flex flex-col gap-3">
            <Button
              type="submit"
              class="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-md font-semibold text-center"
              disabled={state.submitting || !state.domainInput.trim()}
            >
              {state.submitting ? 'Adding Domain...' : 'Add Domain'}
            </Button>
            <Button
              href="/calendar/page-builder?modal=success"
              type="button"
              class="text-sm text-gray-500 hover:text-gray-700 underline text-center"
            >
              Skip for now
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CalendarReady({ domain }: { domain: string }) {
  return (
    <div class="flex flex-col h-full">
      <div class="flex-1 flex flex-col justify-center items-center gap-8">
        <StatusPill text="Setup complete" theme="green" />

        {/* Success icon with calendar badge */}
        <div class="relative">
          <div class="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <IcoCheck class="w-10 h-10 text-green-600" />
          </div>
          <div class="absolute -bottom-2 -right-2 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
            <IcoCalendar class="w-4 h-4 text-white" />
          </div>
        </div>

        <div class="max-w-md">
          <h2 class="text-3xl font-bold text-gray-900 mb-3">Your calendar is ready!</h2>
          <p class="text-gray-600 leading-relaxed mb-6">
            Your calendar page is now live and ready to accept bookings. You can start sharing your
            link or manage your domains.
          </p>

          {/* Calendar URL display */}
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <div class="text-sm text-gray-500 mb-2">Your calendar is live at:</div>
            <div class="flex items-center gap-2">
              <Button
                href={`https://${domain}`}
                class="text-indigo-600 hover:text-indigo-700 font-mono text-sm underline decoration-transparent hover:decoration-current transition-colors"
              >
                {domain}
                <IcoExternalLink class="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>

        {/* Call to action buttons */}
        <div class="flex flex-col gap-3 w-full max-w-sm mx-auto">
          <Button
            href="/calendar/page-builder?modal=success"
            class="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-md font-semibold text-center"
          >
            Return to page builder
          </Button>
          <Button
            href="/calendar"
            class="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-6 rounded-md font-semibold text-center"
          >
            Go to My Calendar
          </Button>
        </div>
      </div>
    </div>
  );
}

function DomainPane() {
  const domainsData = useAsyncData(async () => rpx.domains.getAccountDomains(), []);

  const [state, setState] = useState<DomainUIState>({
    showDomainForm: false,
    domainInput: '',
    submitting: false,
    addedDomains: [],
  });

  const domains = domainsData?.data?.domains || [];
  const defaultDomain = [...state.addedDomains, ...domains].find((x) => x.isDefault);

  const handleAddDomain = async (e: Event) => {
    e.preventDefault();
    if (!state.domainInput.trim()) {
      return;
    }

    setState((prev) => ({ ...prev, submitting: true }));
    try {
      const loadedDomains = domainsData?.data?.domains || [];
      const newDomain = await rpx.domains.addAccountDomain({
        domain: state.domainInput.trim(),
        isDefault: loadedDomains.length === 0 && state.addedDomains.length === 0,
      });

      setState({
        showDomainForm: false,
        domainInput: '',
        submitting: false,
        addedDomains: [...state.addedDomains, newDomain],
      });
      showToast({ title: 'Success', message: 'Domain added successfully', type: 'ok' });
    } catch (error) {
      setState((prev) => ({ ...prev, submitting: false }));
      showToast({ title: 'Error', message: 'Failed to add domain', type: 'warn' });
    }
  };

  if (domainsData.isLoading) {
    return <Loading />;
  }

  if (state.showDomainForm || !defaultDomain) {
    return <DomainForm state={state} setState={setState} handleAddDomain={handleAddDomain} />;
  }

  return <CalendarReady domain={defaultDomain.domain} />;
}

export function DomainStep() {
  const currentUser = useCurrentUser();

  if (!currentUser) {
    return <GoToLogin />;
  }

  return <DomainPane />;
}
